import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ICollectionSlot } from '@lootboy/lib/slices/cardCollections/types';
import { ICardTemplate } from '@lootboy/lib/types/cards';
import { range } from '@lootboy/lib/utils/arrays';

import Image from '~/components/Image';

import SpecialGroupsCanvas from '~/views/CollectionView/CollectionCardGrid/CollectionGridSpecialGroup/SpecialGroupCanvas';
import { getBorderPosition } from '~/views/CollectionView/CollectionCardGrid/utils';

import constants from '~/sass/consts.module.scss';

import styles from './CollectionGridSpecialGroup.module.scss';

export type TExtendedSlot = ICollectionSlot & {
  insertedCard: {
    cardId: string | null;
    cardTemplate: ICardTemplate;
  } | null;
};

interface ICollectionGridSpecialGroupProps {
  specialGroup: { id: string; image: string };
  firstCardPlacement: { column: number; row: number };
  lastCardPlacement: { column: number; row: number };
  slots: Array<TExtendedSlot>;
}

const CollectionGridSpecialGroup: React.FC<
  ICollectionGridSpecialGroupProps
> = ({ specialGroup, firstCardPlacement, lastCardPlacement, slots }) => {
  const specialGroupRef = useRef<HTMLDivElement | null>(null);
  const backgroundImageRef = useRef<HTMLImageElement>(null);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= Number(constants.mediumMobileView),
  );

  // special group dimensions are needed for calculation of the absolute placement for
  // border elements using `getBorderPosition()`
  const [specialGroupDimensions, setSpecialGroupDimensions] = useState<{
    height: number | null;
    width: number | null;
  }>({
    height: null,
    width: null,
  });

  const measureSpecialGroupContainer = useCallback(
    (element: HTMLDivElement) => {
      const { width, height } = element.getBoundingClientRect();
      setSpecialGroupDimensions({
        height,
        width,
      });
    },
    [],
  );

  // set special group dimensions on first paint
  const onSpecialGroupPaint = useCallback(
    (specialGroupDiv: HTMLDivElement) => {
      if (!specialGroupDiv) {
        return;
      }
      specialGroupRef.current = specialGroupDiv;
      measureSpecialGroupContainer(specialGroupDiv);
    },
    [measureSpecialGroupContainer],
  );

  // recalculate special group dimensions no window resize
  useEffect(() => {
    const measure = () => {
      if (!specialGroupRef.current) {
        return;
      }
      measureSpecialGroupContainer(specialGroupRef.current);
      setIsMobile(window.innerWidth <= Number(constants.mediumMobileView));
    };
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
    };
  }, [measureSpecialGroupContainer]);

  const verticalBorders = lastCardPlacement.column - firstCardPlacement.column;
  const horizontalBorders = lastCardPlacement.row - firstCardPlacement.row;

  const [verticalRange, horizontalRange] = useMemo(
    () => [range(verticalBorders), range(horizontalBorders)],
    [verticalBorders, horizontalBorders],
  );

  const gridPlacementStyle = useMemo(() => {
    return {
      gridColumn: `${firstCardPlacement.column} / ${
        lastCardPlacement.column + 1
      }`,
      gridRow: `${firstCardPlacement.row} / ${lastCardPlacement.row + 1}`,
    };
  }, [
    firstCardPlacement.column,
    firstCardPlacement.row,
    lastCardPlacement.column,
    lastCardPlacement.row,
  ]);

  return (
    <>
      <div
        ref={onSpecialGroupPaint}
        style={gridPlacementStyle}
        className={styles.specialGroup}
      >
        {verticalRange.map((i) => {
          if (!specialGroupDimensions.width) {
            return null;
          }
          return (
            <div
              key={i}
              className={styles.verticalBorder}
              style={{
                left: getBorderPosition({
                  index: i,
                  numberOfSegments: verticalBorders + 1,
                  parentSize: specialGroupDimensions.width,
                  isMobile,
                }),
              }}
            />
          );
        })}
        {horizontalRange.map((i) => {
          if (!specialGroupDimensions.height) {
            return null;
          }
          return (
            <div
              key={i}
              className={styles.horizontalBorder}
              style={{
                top: getBorderPosition({
                  index: i,
                  numberOfSegments: horizontalBorders + 1,
                  parentSize: specialGroupDimensions.height,
                  isMobile,
                }),
              }}
            />
          );
        })}
      </div>
      <Image
        className={styles.backgroundImage}
        ref={backgroundImageRef}
        path={specialGroup.image}
        alt="Special group background"
        style={gridPlacementStyle}
        loading="eager"
        onLoad={() => {
          if (!specialGroupRef.current) {
            return;
          }
          measureSpecialGroupContainer(specialGroupRef.current);
        }}
      />
      {specialGroupDimensions.height && specialGroupDimensions.width && (
        <SpecialGroupsCanvas
          height={specialGroupDimensions.height}
          width={specialGroupDimensions.width}
          imgElement={backgroundImageRef.current}
          slotPlacement={slots.map((slot) =>
            Boolean(slot.insertedCard?.cardId),
          )}
          numberOfColumns={verticalBorders + 1}
          style={gridPlacementStyle}
        />
      )}
    </>
  );
};

export default CollectionGridSpecialGroup;
